import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LogOut, User as UserIcon, AlertCircle, Plus, History, X, Play, Clock, Check, Loader2, StopCircle } from 'lucide-react';
import { useApp, ReviewProcessStep } from '../context/AppContext';
import { projectsApi } from '../services/api';
import { STEPS } from '../constants';
import { ProjectStatus, getProjectStatusDisplayText } from '../types';

interface LayoutProps {
  children?: React.ReactNode;
}

interface HistoryItem {
  id: string;
  name: string;
  status: string;
  created_at: string;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: projectId } = useParams<{ id: string }>();
  const { user, currentProject, logout, startReview, clearCurrentProject, reviewProcess } = useApp();
  
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showNewReviewModal, setShowNewReviewModal] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [startingReview, setStartingReview] = useState(false);

  const getReviewStepStatus = (stepKey: ReviewProcessStep): 'pending' | 'active' | 'completed' | 'failed' => {
    const order: ReviewProcessStep[] = ['uploading', 'splitting', 'reviewing', 'reporting', 'exporting'];
    const ci = order.indexOf(reviewProcess.currentStep);
    const si = order.indexOf(stepKey);
    if (reviewProcess.currentStep === 'failed') return si < ci ? 'completed' : si === ci ? 'failed' : 'pending';
    if (reviewProcess.currentStep === 'completed') return 'completed';
    if (reviewProcess.currentStep === 'idle') return 'pending';
    return si < ci ? 'completed' : si === ci ? 'active' : 'pending';
  };

  const isReviewProcessActive = reviewProcess.currentStep !== 'idle' && reviewProcess.currentStep !== 'completed' && reviewProcess.currentStep !== 'failed';

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  // Load project history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
      const response = await fetch(`${baseUrl}/projects?page_size=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setHistoryItems(data.items.map((item: any) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          created_at: item.created_at,
        })));
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    }
    setLoadingHistory(false);
  };

  const handleShowHistory = () => {
    setShowHistoryModal(true);
    loadHistory();
  };

  const handleSelectProject = (id: string) => {
    setShowHistoryModal(false);
    navigate(`/project/${id}/upload`);
  };

  const handleNewProject = () => {
    setShowHistoryModal(false);
    clearCurrentProject();
    navigate('/project/new/upload');
  };

  const handleCancelProject = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await projectsApi.cancel(id);
      loadHistory();
      if (id === projectId) {
        // reload if current project was cancelled
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to cancel project:', error);
    }
  };

  // Start new review for current project
  const handleStartNewReview = async () => {
    if (!projectId || projectId === 'new') return;
    
    setStartingReview(true);
    try {
      await startReview(projectId);
      setShowNewReviewModal(false);
      navigate(`/project/${projectId}/review`);
    } catch (e) {
      console.error('Failed to start review:', e);
    }
    setStartingReview(false);
  };

  const isProjectRoute = location.pathname.includes('/project/');

  // Determine which steps are accessible based on project status
  const getStepStatus = (stepPath: string): 'active' | 'completed' | 'disabled' | 'current' => {
    const currentPath = location.pathname;
    
    // 如果当前路径包含该步骤，标记为 current
    if (currentPath.includes(stepPath)) return 'current';
    
    // 如果没有项目或者是新项目，只允许访问上传页面
    if (!currentProject || projectId === 'new') {
      if (stepPath === '/upload') return 'active';
      return 'disabled';
    }
    
    const status = currentProject.status;
    
    switch (stepPath) {
      case '/upload':
        return 'completed';
      case '/split':
        if ([ProjectStatus.PARSED, ProjectStatus.REVIEWED, ProjectStatus.COMPLETED].includes(status as ProjectStatus)) {
          return 'completed';
        }
        if ([ProjectStatus.FILES_UPLOADED, ProjectStatus.PARSING].includes(status as ProjectStatus)) {
          return 'active';
        }
        return 'disabled';
      case '/exemptions':
        if ([ProjectStatus.REVIEWED, ProjectStatus.COMPLETED].includes(status as ProjectStatus)) {
          return 'completed';
        }
        if ([ProjectStatus.PARSED, ProjectStatus.REVIEWING].includes(status as ProjectStatus)) {
          return 'active';
        }
        return 'disabled';
      case '/review':
        if ([ProjectStatus.REVIEWED, ProjectStatus.COMPLETED].includes(status as ProjectStatus)) {
          return 'completed';
        }
        if ([ProjectStatus.PARSED, ProjectStatus.REVIEWING].includes(status as ProjectStatus)) {
          return 'active';
        }
        return 'disabled';
      case '/report':
        if (status === ProjectStatus.COMPLETED) {
          return 'completed';
        }
        if ([ProjectStatus.REVIEWED].includes(status as ProjectStatus)) {
          return 'active';
        }
        return 'disabled';
      case '/export':
        if (status === ProjectStatus.COMPLETED) {
          return 'completed';
        }
        if ([ProjectStatus.REVIEWED].includes(status as ProjectStatus)) {
          return 'active';
        }
        return 'disabled';
      default:
        return 'disabled';
    }
  };

  // Check if can start new review
  const canStartNewReview = currentProject && 
    [ProjectStatus.PARSED, ProjectStatus.REVIEWED, ProjectStatus.COMPLETED].includes(currentProject.status as ProjectStatus);

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 fixed inset-y-0 left-0 z-10 flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-2 font-bold text-xl text-slate-800 tracking-tight">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white">
              AI
            </div>
            <span>法务<span className="text-slate-500">AI助手</span></span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-4 py-3 border-b border-slate-100 flex gap-2">
          <button
            onClick={handleNewProject}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <Plus size={14} />
            新项目
          </button>
          <button
            onClick={handleShowHistory}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <History size={14} />
            历史
          </button>
        </div>

        {/* Project Info */}
        {currentProject && (
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400 uppercase font-semibold mb-1">当前项目</p>
            <p className="text-sm font-medium text-slate-800 truncate">{currentProject.name}</p>
            <p className="text-xs text-slate-500 mt-1">
              状态: {getProjectStatusDisplayText(currentProject.status as ProjectStatus)}
            </p>
            {/* New Review Button */}
            {canStartNewReview && (
              <button
                onClick={() => setShowNewReviewModal(true)}
                className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
              >
                <Play size={12} />
                重新审查
              </button>
            )}
          </div>
        )}

        {/* Project Navigation */}
        <div className="flex-1 py-4 px-4 space-y-1 overflow-y-auto">
          {isProjectRoute ? (
            <>
              <div className="px-2 mb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center justify-between">
                <span>审查流程</span>
                {/* 审查状态指示 */}
                {isReviewProcessActive && (
                  <div className="flex items-center gap-1.5">
                    <div className="relative">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping" />
                    </div>
                    <span className="text-blue-600 normal-case font-medium">进行中</span>
                  </div>
                )}
                {reviewProcess.currentStep === 'completed' && (
                  <span className="text-emerald-600 normal-case font-medium flex items-center gap-1">
                    <Check size={12} /> 完成
                  </span>
                )}
                {reviewProcess.currentStep === 'failed' && (
                  <span className="text-rose-600 normal-case font-medium flex items-center gap-1">
                    <X size={12} /> 失败
                  </span>
                )}
              </div>
              
              {/* 合并后的步骤列表 - 既是导航按钮也显示进度 */}
              {STEPS.map((step) => {
                const stepStatus = getStepStatus(step.path);
                const isActive = location.pathname.includes(step.path);
                const isDisabled = stepStatus === 'disabled';
                
                // 获取当前步骤的审查进度状态
                const reviewStepKey = step.path === '/upload' ? 'uploading' : 
                                      step.path === '/split' ? 'splitting' :
                                      step.path === '/exemptions' ? null :
                                      step.path === '/review' ? 'reviewing' :
                                      step.path === '/report' ? 'reporting' :
                                      step.path === '/export' ? 'exporting' : null;
                const reviewStatus = reviewStepKey ? getReviewStepStatus(reviewStepKey as ReviewProcessStep) : null;
                const isReviewActive = reviewStatus === 'active';
                const isReviewDone = reviewStatus === 'completed';
                const isReviewFailed = reviewStatus === 'failed';
                
                return (
                  <button
                    key={step.id}
                    onClick={() => !isDisabled && navigate(`/project/${projectId}${step.path}`)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      isActive 
                        ? 'bg-slate-900 text-white shadow-sm' 
                        : isReviewActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : isDisabled
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all ${
                      isActive 
                        ? 'border-transparent bg-slate-700' 
                        : isReviewActive
                          ? 'bg-blue-500 text-white border-transparent'
                          : isReviewDone || stepStatus === 'completed'
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : isReviewFailed
                              ? 'border-rose-500 bg-rose-500 text-white'
                              : isDisabled
                                ? 'border-slate-200 bg-slate-100'
                                : 'border-slate-300 bg-white'
                    }`}>
                      {isReviewActive ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : isReviewDone || stepStatus === 'completed' ? (
                        <Check size={12} />
                      ) : isReviewFailed ? (
                        <X size={12} />
                      ) : (
                        step.id
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <span>{step.label}</span>
                      {/* 进度条 - 仅在当前步骤活跃时显示 */}
                      {isReviewActive && (
                        <div className="mt-1 h-1 bg-blue-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 transition-all duration-300" 
                            style={{ width: `${reviewProcess.stepProgress}%` }} 
                          />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              
              {/* 错误信息 */}
              {reviewProcess.errorMessage && (
                <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded-lg">
                  <p className="text-xs text-rose-600">{reviewProcess.errorMessage}</p>
                </div>
              )}
            </>
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">
              请选择一个项目开始
            </div>
          )}
        </div>

        {/* Error Display */}
        {currentProject?.error_message && (
          <div className="mx-4 mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
            <div className="flex items-start gap-2 text-rose-700">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <p className="text-xs">{currentProject.error_message}</p>
            </div>
          </div>
        )}

        {/* User Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600">
              <UserIcon size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.name || '演示用户'}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {user?.email || 'demo@tpcn.com'}
              </p>
            </div>
          </div>
          <button 
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:text-red-600 transition-colors"
          >
            <LogOut size={14} />
            退出登录
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 h-screen overflow-hidden flex flex-col">
        {children}
      </main>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">历史项目</h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
                </div>
              ) : historyItems.length > 0 ? (
                <div className="space-y-2">
                  {historyItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelectProject(item.id)}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        item.id === projectId
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{item.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                              item.status === 'REVIEWED' ? 'bg-blue-100 text-blue-700' :
                              item.status === 'FAILED' ? 'bg-rose-100 text-rose-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {getProjectStatusDisplayText(item.status as ProjectStatus)}
                            </span>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock size={10} />
                              {new Date(item.created_at).toLocaleDateString('zh-CN')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(item.status === 'parsing' || item.status === 'reviewing' || item.status === 'report_generating') && (
                            <button
                              onClick={(e) => handleCancelProject(item.id, e)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                              title="停止处理"
                            >
                              <StopCircle size={16} />
                            </button>
                          )}
                          {item.id === projectId && (
                            <span className="text-xs text-emerald-600 font-medium whitespace-nowrap">当前</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <History size={32} className="mx-auto mb-2 opacity-50" />
                  <p>暂无历史项目</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200">
              <button
                onClick={handleNewProject}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
              >
                <Plus size={16} />
                创建新项目
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Review Confirmation Modal */}
      {showNewReviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">重新审查</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600 mb-4">
                确定要对当前项目 <span className="font-medium text-slate-900">{currentProject?.name}</span> 重新进行 AI 审查吗？
              </p>
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ 重新审查将覆盖之前的审查结果，此操作不可撤销。
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => setShowNewReviewModal(false)}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 font-medium"
              >
                取消
              </button>
              <button
                onClick={handleStartNewReview}
                disabled={startingReview}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
              >
                {startingReview ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    开始中...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    开始审查
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
